import type { ReactNode } from 'react';

import { cn } from '../../design-system/cn.js';
import { InfoTooltip } from '../../design-system/form.js';

export function PageHeader({
  action,
  description,
  eyebrow,
  title,
}: {
  action?: ReactNode;
  description?: ReactNode;
  eyebrow?: string;
  title: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-col gap-4 border-b border-border pb-5 sm:mb-6 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-accent">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-balance text-[clamp(1.875rem,8vw,2.25rem)] font-semibold leading-tight tracking-[-0.035em] text-ink sm:text-4xl">
          {title}
        </h1>
        {description ? (
          <p className="mt-2 max-w-2xl text-base leading-6 text-ink-2">{description}</p>
        ) : null}
      </div>
      {action ? (
        <div className="w-full [&>*]:w-full sm:w-auto sm:[&>*]:w-auto md:shrink-0">{action}</div>
      ) : null}
    </header>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-ink-3">
      {children}
    </h2>
  );
}

export function SegmentedControl<T extends string>({
  info,
  label,
  onChange,
  options,
  value,
}: {
  info?: ReactNode;
  label: string;
  onChange: (value: T) => void;
  options: readonly { label: string; value: T }[];
  value: T;
}) {
  return (
    <fieldset>
      <legend className="mb-2 font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
        <span className="inline-flex items-center gap-1">
          {label}
          {info ? <InfoTooltip label={`About ${label}`}>{info}</InfoTooltip> : null}
        </span>
      </legend>
      <div className="inline-flex w-full rounded-sm border border-border bg-bg p-1 sm:w-auto">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-11 flex-1 rounded-[5px] px-4 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-ring sm:flex-none',
              value === option.value
                ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1px_var(--accent-line)]'
                : 'text-ink-2 hover:bg-surface hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}
